import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface NavigationBlockerContextValue {
  /** Register a blocker - returns unregister function */
  registerBlocker: (id: string, hasChanges: boolean, message?: string) => () => void;
  /** Check if any blockers are active */
  hasActiveBlockers: () => boolean;
}

const NavigationBlockerContext = createContext<NavigationBlockerContextValue | null>(null);

interface BlockerState {
  id: string;
  hasChanges: boolean;
  message: string;
}

interface PendingNavigation {
  to: string;
  from: string;
}

/**
 * Provider component that enables navigation blocking throughout the app.
 * Works with BrowserRouter by intercepting navigation attempts.
 */
export const NavigationBlockerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [blockers, setBlockers] = useState<Map<string, BlockerState>>(new Map());
  const blockersRef = useRef(blockers);

  const [pendingNavigation, setPendingNavigation] = useState<PendingNavigation | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Keep ref in sync with state for stable callbacks
  useEffect(() => {
    blockersRef.current = blockers;
  }, [blockers]);

  const registerBlocker = useCallback(
    (id: string, hasChanges: boolean, message = 'You have unsaved changes. Are you sure you want to leave?') => {
      setBlockers((prev) => {
        const existing = prev.get(id);
        if (existing && existing.hasChanges === hasChanges && existing.message === message) {
          return prev; // No change — return same reference to skip state update
        }
        const next = new Map(prev);
        next.set(id, { id, hasChanges, message });
        return next;
      });

      // Return unregister function
      return () => {
        setBlockers((prev) => {
          if (!prev.has(id)) return prev; // Already removed
          const next = new Map(prev);
          next.delete(id);
          return next;
        });
      };
    },
    []
  );

  // Read from ref so this callback is stable (never recreated)
  const hasActiveBlockers = useCallback(() => {
    return Array.from(blockersRef.current.values()).some((b) => b.hasChanges);
  }, []);

  // Read from ref so this callback is stable (never recreated)
  const getBlockerMessage = useCallback(() => {
    const activeBlocker = Array.from(blockersRef.current.values()).find((b) => b.hasChanges);
    return activeBlocker?.message || 'You have unsaved changes. Are you sure you want to leave?';
  }, []);

  // Handle navigation confirmation
  const handleConfirmNavigation = useCallback(() => {
    if (pendingNavigation) {
      // Temporarily clear all blockers to allow navigation
      setBlockers(new Map());
      navigate(pendingNavigation.to);
      setPendingNavigation(null);
      setShowDialog(false);
    }
  }, [pendingNavigation, navigate]);

  // Handle navigation cancellation
  const handleCancelNavigation = useCallback(() => {
    setPendingNavigation(null);
    setShowDialog(false);
  }, []);

  // Intercept navigation using history API
  useEffect(() => {
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function (
      data: unknown,
      unused: string,
      url?: string | URL | null
    ) {
      if (hasActiveBlockers() && url) {
        const targetUrl = typeof url === 'string' ? url : url?.toString() || '';
        const currentPath = window.location.pathname + window.location.search;

        // Only block if actually navigating away (not just hash changes)
        if (targetUrl && targetUrl !== currentPath) {
          const confirmed = window.confirm(getBlockerMessage());
          if (!confirmed) {
            return undefined;
          }
        }
      }
      return originalPushState.call(this, data, unused, url);
    };

    window.history.replaceState = function (
      data: unknown,
      unused: string,
      url?: string | URL | null
    ) {
      // Replace state doesn't navigate, so we don't block it
      return originalReplaceState.call(this, data, unused, url);
    };

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, [hasActiveBlockers, getBlockerMessage]);

  // Track popstate (back/forward buttons)
  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      if (hasActiveBlockers()) {
        e.preventDefault();
        const confirmed = window.confirm(getBlockerMessage());
        if (!confirmed) {
          // Push the current state back to prevent navigation
          window.history.pushState(null, '', location.pathname + location.search);
          return undefined;
        }
        // Allow navigation by not interfering
      }
      return undefined;
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [hasActiveBlockers, getBlockerMessage, location]);

  // Track link clicks
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a');

      if (!link) return;

      const href = link.getAttribute('href');
      if (!href) return;

      // Only block internal navigation
      if (href.startsWith('http') || href.startsWith('//')) return;
      if (href.startsWith('#')) return;
      if (href.startsWith('mailto:') || href.startsWith('tel:')) return;

      // Check if it's a navigation away
      const currentPath = location.pathname + location.search;
      if (href === currentPath) return;

      if (hasActiveBlockers()) {
        e.preventDefault();
        e.stopPropagation();

        setPendingNavigation({
          from: currentPath,
          to: href,
        });
        setShowDialog(true);
      }
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [hasActiveBlockers, location]);

  // Both registerBlocker and hasActiveBlockers are stable (no deps),
  // so contextValue is created once and never changes.
  const contextValue = useMemo(
    () => ({ registerBlocker, hasActiveBlockers }),
    [registerBlocker, hasActiveBlockers]
  );

  return (
    <NavigationBlockerContext.Provider value={contextValue}>
      {children}

      <AlertDialog open={showDialog} onOpenChange={setShowDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              {getBlockerMessage()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelNavigation}>
              Stay on Page
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmNavigation}
              className="bg-red-600 hover:bg-red-700"
            >
              Leave Page
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </NavigationBlockerContext.Provider>
  );
};

/**
 * Hook to register a navigation blocker.
 * Returns a function to update the blocker state.
 *
 * @example
 * ```tsx
 * const { setHasUnsavedChanges } = useNavigationBlocker('my-form');
 * setHasUnsavedChanges(true, 'You have unsaved changes. Leave anyway?');
 * ```
 */
export const useNavigationBlocker = (id: string) => {
  const context = useContext(NavigationBlockerContext);
  if (!context) {
    throw new Error('useNavigationBlocker must be used within NavigationBlockerProvider');
  }

  // Destructure to get stable references — avoids depending on `context` object
  const { registerBlocker, hasActiveBlockers } = context;

  const [hasChanges, setHasChanges] = useState(false);
  const [message, setMessage] = useState('You have unsaved changes. Are you sure you want to leave?');

  useEffect(() => {
    const unregister = registerBlocker(id, hasChanges, message);
    return unregister;
  }, [registerBlocker, id, hasChanges, message]);

  // Memoize setHasUnsavedChanges to prevent infinite loops in consumers
  const setHasUnsavedChanges = useCallback(
    (hasUnsaved: boolean, msg?: string) => {
      setHasChanges(hasUnsaved);
      if (msg) setMessage(msg);
    },
    [],
  );

  return {
    setHasUnsavedChanges,
    hasActiveBlockers,
  };
};

export default NavigationBlockerProvider;
